import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; 
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PortfolioSection = () => {
  const PortfolioItems = [
    {
      id: 1,
      title: "E-Commerce Platform",
      category: "Web Development",
      description: "A comprehensive e-commerce solution with advanced inventory management, payment processing, and analytics dashboard.",
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["React", "Node.js", "MongoDB", "Stripe"],
      link: "/Portfolio"
    },
    {
      id: 2,
      title: "Healthcare Management App",
      category: "Mobile Development",
      description: "Mobile application for healthcare providers to manage patient records, appointments, and medical history.",
      image: "https://cdn.dribbble.com/userupload/32029538/file/original-8a595280f2de2fdbd14f748dbe7e72f5.png?resize=400x0",
      technologies: ["React Native", "Firebase", "Redux", "TypeScript"],
      link: "/Portfolio"
    },
    {
      id: 3,
      title: "Financial Dashboard",
      category: "Web Development",
      description: "Real-time financial analytics dashboard with interactive charts, reporting tools, and data visualization.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["Vue.js", "D3.js", "Python", "PostgreSQL"],
      link: "/Portfolio"
    },
    {
      id: 4,
      title: "Learning Management System",
      category: "Web Development",
      description: "Comprehensive LMS platform with course management, student tracking, and interactive learning modules.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["Angular", "Express.js", "MySQL", "AWS"],
      link: "/Portfolio"
    },
    {
      id: 5,
      title: "Restaurant Ordering App",
      category: "Mobile Development",
      description: "Mobile app for restaurant ordering with real-time tracking, payment integration, and loyalty rewards.",
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      technologies: ["Flutter", "Firebase", "Stripe", "Google Maps"],
      link: "/Portfolio"
    },
    {
      id: 6,
      title: "IoT Monitoring System",
      category: "Cloud Solutions",
      description: "IoT device monitoring and control system with real-time data processing and alert management.",
      image: "https://asimily.com/wp-content/uploads/2024/11/11-Common-IoT-Devices-That-Are-Vulnerable-to-Hacking.png",
      technologies: ["React", "AWS IoT", "Lambda", "DynamoDB"],
      link: "/Portfolio"
    }
  ];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Web Development': return 'bg-blue-600 text-white';
      case 'Mobile Development': return 'bg-purple-600 text-white';
      case 'Cloud Solutions': return 'bg-green-600 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  return (
    <section className="py-20 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section: Simple Fade-in without slide */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-extrabold text-text-primary mb-4 tracking-tight">
            Our Masterpieces
          </h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-6"></div>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            We don't just build apps; we create digital experiences that drive growth and innovation.
          </p>
        </motion.div>

        {/* Grid Section: Horizontal Reveal or Scale-up */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {PortfolioItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }} // નાનામાંથી મોટું થશે
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -10 }} // હોવર કરવાથી ઉપર જશે
              className="relative group bg-surface rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-300"
            >
              {/* Image Container */}
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <span className="text-white text-sm font-medium">View Project Details</span>
                </div>
                
                <div className="absolute top-4 left-4">
                  <span className={`px-4 py-1 text-xs font-bold uppercase tracking-wider rounded-full shadow-lg ${getCategoryColor(item.category)}`}>
                    {item.category}
                  </span>
                </div>
              </div>

              {/* Content Body */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-text-primary mb-3 group-hover:text-primary transition-colors">
                  {item.title}
                </h3>
                <p className="text-text-secondary text-sm leading-relaxed mb-6 line-clamp-2">
                  {item.description}
                </p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {item.technologies.map((tech, idx) => (
                    <span key={idx} className="text-[10px] px-2 py-1 bg-gray-100 text-gray-600 font-bold uppercase rounded border border-gray-200">
                      {tech}
                    </span>
                  ))}
                </div>

                <Link 
                  to={item.link} 
                  className="group/btn inline-flex items-center text-sm font-bold text-primary"
                >
                  <span className="mr-2">EXPLORE CASE STUDY</span>
                  <div className="p-2 rounded-full bg-primary/10 group-hover/btn:bg-primary group-hover/btn:text-white transition-all">
                    <Icon name="ArrowRight" size={14} />
                  </div>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;